import { Alert, Button, Flex, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconAlertTriangle, IconDownload, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import {
  checkHacomSignPlugin,
  HacomSignPluginCheck,
  requestHacomSignUpdate,
} from "../_base/helper/HacomSignPlugin";
import { NotificationExtension } from "../_base/extension/NotificationExtension";

const MODAL_ID = "hacomsign-plugin-gate";

interface PluginGateProps {
  initial: HacomSignPluginCheck;
  onInstallGuide: () => void;
  onReady: (check: HacomSignPluginCheck) => void;
}

// Bản HacomSign cũ ký sai hoặc thiếu kiểm tra, nên phải cài/cập nhật đúng bản tối thiểu mới được ký số
function PluginGate({ initial, onInstallGuide, onReady }: PluginGateProps) {
  const [check, setCheck] = useState(initial);
  const [checking, setChecking] = useState(false);
  const [updateRequested, setUpdateRequested] = useState(false);

  const recheck = async () => {
    setChecking(true);
    try {
      const next = await checkHacomSignPlugin();
      setCheck(next);
      if (next.status === "ready") {
        modals.close(MODAL_ID);
        NotificationExtension.Success(
          `HacomSign ${next.installedVersion} đã sẵn sàng, bấm "Ký số" để ký.`
        );
        onReady(next);
      } else NotificationExtension.Fails("HacomSign vẫn chưa đạt bản yêu cầu.");
    } finally {
      setChecking(false);
    }
  };

  const updateNow = async () => {
    const ok = await requestHacomSignUpdate();
    if (ok) setUpdateRequested(true);
    else NotificationExtension.Fails("HacomSign không mở được hộp thoại cập nhật, hãy tải bản mới.");
  };

  const notRunning = check.status === "notRunning";
  // Có số bản tức là bản >= 1.0.7, đã có /update để cập nhật từ trình duyệt
  const canUpdateInPlace = !notRunning && !!check.installedVersion;

  return (
    <Stack gap="md">
      <Alert
        color={notRunning ? "red" : "orange"}
        icon={<IconAlertTriangle />}
        title={notRunning ? "Chưa kết nối được HacomSign" : "Cần cập nhật HacomSign"}
      >
        {notRunning ? (
          <Text size="sm">
            Máy chưa cài hoặc chưa mở phần mềm ký số HacomSign. Hãy mở HacomSign (biểu tượng ở góc
            phải thanh taskbar), hoặc tải và cài đặt nếu máy chưa có.
          </Text>
        ) : (
          <Text size="sm">
            Máy đang dùng HacomSign{" "}
            <strong>{check.installedVersion ?? "1.0.6 trở về trước"}</strong>, cần bản{" "}
            <strong>{check.minVersion}</strong> trở lên mới ký số được.
          </Text>
        )}
      </Alert>

      {!notRunning && !canUpdateInPlace && (
        <Text size="sm" c="dimmed">
          Cách cập nhật: tải bản mới bên dưới và cài đè, hoặc chuột phải biểu tượng HacomSign ở góc
          màn hình → <strong>Cập nhật</strong>.
        </Text>
      )}
      {updateRequested && (
        <Text size="sm" c="dimmed">
          HacomSign đang mở hộp thoại cập nhật. Cập nhật xong, bấm "Tôi đã cập nhật, kiểm tra lại".
        </Text>
      )}

      <Flex gap="sm" justify="flex-end" wrap="wrap">
        <Button variant="subtle" onClick={onInstallGuide}>
          Xem hướng dẫn cài đặt
        </Button>
        {canUpdateInPlace ? (
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={updateNow}>
            Cập nhật ngay
          </Button>
        ) : (
          <Button
            variant="light"
            component="a"
            href={check.downloadUrl}
            leftSection={<IconDownload size={16} />}
          >
            Tải bản mới
          </Button>
        )}
        <Button loading={checking} onClick={recheck}>
          {notRunning ? "Đã mở HacomSign, kiểm tra lại" : "Tôi đã cập nhật, kiểm tra lại"}
        </Button>
      </Flex>
    </Stack>
  );
}

export function openHacomSignPluginModal(
  check: HacomSignPluginCheck,
  onInstallGuide: () => void,
  onReady: (check: HacomSignPluginCheck) => void
) {
  modals.open({
    modalId: MODAL_ID,
    title: "Phần mềm ký số HacomSign",
    size: "lg",
    children: (
      <PluginGate
        initial={check}
        onInstallGuide={() => {
          modals.close(MODAL_ID);
          onInstallGuide();
        }}
        onReady={onReady}
      />
    ),
  });
}
